"use client";

import { Music, Instagram, Pencil, Trash2 } from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/auth-context";
import { formatCurrency, formatDateShort } from "@/lib/format";
import type { Event } from "@/types/events";

interface EventDetailModalProps {
  event: Event;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  deleting?: boolean;
}

const STATUS_CONFIG: Record<string, { label: string; pip: string; badge: string }> = {
  planned:   { label: "Aguardando",  pip: "bg-[#f5b731]",  badge: "bg-[#f5b731]/15 text-[#f5b731]" },
  confirmed: { label: "Confirmado",  pip: "bg-[#3ed97a]",  badge: "bg-[#3ed97a]/15 text-[#3ed97a]" },
  completed: { label: "Realizado",   pip: "bg-primary",    badge: "bg-primary/15 text-primary" },
  cancelled: { label: "Cancelado",   pip: "bg-destructive", badge: "bg-destructive/10 text-destructive" },
};


function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-3 text-2xs font-bold uppercase tracking-[0.1em] text-muted-foreground/60">
      {children}
    </p>
  );
}

export function EventDetailModal({ event, onClose, onEdit, onDelete, deleting }: EventDetailModalProps) {
  const { user } = useAuth();
  const isSuperOrAdmin = user?.role === "super" || user?.role === "admin";

  const status = STATUS_CONFIG[event.status] ?? STATUS_CONFIG.planned;
  const dateLabel = formatDateShort(event.date);
  const timeLabel = event.start_time.substring(0, 5) + (event.end_time ? ` – ${event.end_time.substring(0, 5)}` : "");

  return (
    <Dialog
      open
      onClose={onClose}
      title={
        <span className="flex items-center gap-2">
          <span className={`size-2 shrink-0 rounded-full ${status.pip}`} />
          {event.name}
        </span>
      }
      footer={
        <>
          {isSuperOrAdmin && (
            <Button
              variant="outline"
              size="icon"
              onClick={onDelete}
              disabled={deleting}
              aria-label="Excluir evento"
              className="shrink-0 border-destructive/30 text-destructive hover:bg-destructive/10 hover:text-destructive"
            >
              <Trash2 className="size-4" />
            </Button>
          )}
          <Button variant="outline" onClick={onClose} className="flex-1">
            Fechar
          </Button>
          {isSuperOrAdmin && (
            <Button onClick={onEdit} className="flex-[2]">
              <Pencil className="size-3.5" />
              Editar
            </Button>
          )}
        </>
      }
    >
      {/* Data e hora */}
      <p className="mb-4 text-13 text-muted-foreground">
        {dateLabel} · {timeLabel}
      </p>

      {/* Status + tipo */}
      <div className="mb-5 flex flex-wrap items-center gap-2">
        <span className={`rounded-full px-2.5 py-1 text-11 font-bold ${status.badge}`}>
          {status.label}
        </span>
        {event.event_type && (
          <Badge variant="secondary">{event.event_type}</Badge>
        )}
        {event.collective_name && (
          <Badge variant="outline">{event.collective_name}</Badge>
        )}
      </div>

      {/* Atrações */}
      {event.attractions.length > 0 && (
        <div className="mb-5">
          <SectionLabel>Atrações</SectionLabel>
          <div className="space-y-2">
            {event.attractions.map((attr) => (
              <div
                key={attr.id}
                className="flex items-center gap-3 rounded-2xl border border-border bg-secondary/40 px-3.5 py-3"
              >
                <div className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-secondary">
                  {attr.attraction_photo_url ? (
                    <img
                      src={attr.attraction_photo_url}
                      alt={attr.attraction_name}
                      className="size-full object-cover"
                    />
                  ) : (
                    <Music className="size-4 text-muted-foreground" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-13 font-semibold text-foreground">
                    {attr.attraction_name}
                  </p>
                  {attr.attraction_instagram && (
                    <p className="flex items-center gap-1 truncate text-11 text-muted-foreground">
                      <Instagram className="size-3 shrink-0" />
                      @{attr.attraction_instagram}
                    </p>
                  )}
                </div>
                {(attr.fee || attr.consumption) && (
                  <div className="shrink-0 text-right">
                    {attr.fee != null && attr.fee > 0 && (
                      <p className="text-11 font-semibold text-foreground">
                        {formatCurrency(attr.fee)}
                      </p>
                    )}
                    {attr.consumption != null && attr.consumption > 0 && (
                      <p className="text-11 text-muted-foreground">
                        consumação {formatCurrency(attr.consumption)}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Estilos musicais */}
      {event.musical_styles.length > 0 && (
        <div className="mb-5">
          <SectionLabel>Estilos musicais</SectionLabel>
          <div className="flex flex-wrap gap-1.5">
            {event.musical_styles.map((style) => (
              <Badge key={style.id} variant="secondary">
                {style.name}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Descrição */}
      {event.description && (
        <div className="mb-5">
          <SectionLabel>Descrição</SectionLabel>
          <p className="text-13 leading-relaxed text-muted-foreground">{event.description}</p>
        </div>
      )}

      {/* Observações */}
      {event.notes && (
        <div className="mb-1">
          <SectionLabel>Observações</SectionLabel>
          <p className="text-13 leading-relaxed text-muted-foreground">{event.notes}</p>
        </div>
      )}
    </Dialog>
  );
}
